import { format } from 'date-fns'
import {
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Cpu,
  GraduationCap,
  Layers,
  Link2,
  RotateCcw,
  Sprout,
  Trash2,
  UserCog,
} from 'lucide-react'
import { type ReactNode, useMemo, useState } from 'react'

import { Badge } from '@/components/admin/shared/badge'
import { cn } from '@/lib/utils'
import { formatCalcLogProvenance } from './_lib/format-calc-log-provenance'
import { KnowledgeMapSnapshot } from './knowledge-map-snapshot'
import { RecalculatingBanner } from './recalculating-banner'
import { useRecommendationEngineMock } from './recommendation-engine-context'
import type {
  ActorKind,
  CalculationLogEntry,
  CalculationRowChange,
  CalculationSource,
  CalculationTargetKind,
  ReasonCode,
} from './types'
import { REASON_CODES } from './types'

const GRADE_LABEL: Record<number, string> = {
  1: 'Again',
  2: 'Hard',
  3: 'Good',
  4: 'Easy',
}

const GROUP_LABEL = {
  subtopic: 'Subtopics',
  topic: 'Topics',
  overall: 'Overall',
  projection: 'Projection',
} as const

/**
 * Human-readable labels for every reason code. Snapshotted in the invariant
 * tests so adding a reason code requires updating BOTH this map AND the
 * REASON_CODES const.
 */
const REASON_LABEL: Record<ReasonCode, string> = {
  student_attempt_easy: 'Easy attempt',
  student_attempt_good: 'Good attempt',
  student_attempt_hard: 'Hard attempt',
  student_attempt_again: 'Again attempt',
  threshold_change: 'Threshold change',
  rollup_weight_change: 'Rollup weight change',
  preset_applied: 'Preset applied',
  target_company_change: 'Target company change',
  baseline_level_change: 'Baseline level change',
  topic_inclusion_change: 'Topic inclusion change',
  subtopic_enable_toggle: 'Subtopic enable toggle',
  weekly_commitment_change: 'Weekly commitment change',
  target_date_change: 'Target date change',
  attempt_reviewed: 'Attempt reviewed',
  coach_seed: 'Knowledge map seeded',
  coach_seed_override: 'Seed override',
  fsrs_recompute: 'FSRS recompute',
  decay_catchup: 'Decay catch-up',
  coach_revert: 'Reverted by coach',
  coach_reapply: 'Re-applied by coach',
  coach_revert_to_snapshot: 'Reverted to snapshot',
  reprocess_cascade: 'Reprocess cascade',
  processing_run_requested: 'Processing run requested',
  settings_snapshot_captured: 'Settings snapshot captured',
  baseline_epoch_change: 'Baseline epoch changed',
  graph_json_saved: 'Graph JSON saved',
}

const SOURCE_ICON: Record<CalculationSource, ReactNode> = {
  attempt: <GraduationCap size={14} />,
  coach_adjustment: <UserCog size={14} />,
  engine_recompute: <Cpu size={14} />,
  seed: <Sprout size={14} />,
  recommendation_lifecycle: <Layers size={14} />,
}

const SOURCE_LABEL: Record<CalculationSource, string> = {
  attempt: 'Student attempt',
  coach_adjustment: 'Coach adjustment',
  engine_recompute: 'Engine recompute',
  seed: 'Knowledge map seed',
  recommendation_lifecycle: 'Recommendation lifecycle',
}

const ACTOR_LABEL: Record<ActorKind, string> = {
  student: 'student',
  coach: 'coach',
  system: 'system',
}

function isAttemptEntry(
  e: CalculationLogEntry,
): e is Extract<CalculationLogEntry, { source: 'attempt' }> {
  return e.source === 'attempt'
}

function RowChange({
  row,
  reverted,
}: {
  row: CalculationRowChange
  reverted: boolean
}) {
  const changed = row.after !== row.before
  const rising = row.after > row.before
  return (
    <div className="bg-background border-border rounded-md border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-foreground text-xs font-semibold">
          {row.targetName}
        </span>
        <span className="flex items-center gap-1.5 text-xs">
          <span className="text-muted-foreground">{row.field}</span>
          <span className="text-muted-foreground tabular-nums">
            {row.before}
          </span>
          <ArrowRight size={12} className="text-muted-foreground" />
          <span
            className={cn(
              'font-semibold tabular-nums',
              reverted
                ? 'text-muted-foreground line-through'
                : !changed
                  ? 'text-muted-foreground'
                  : rising
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-rose-600 dark:text-rose-400',
            )}
          >
            {row.after}
          </span>
        </span>
      </div>

      <p className="text-foreground mt-1 text-[11px] font-medium">
        {row.formulaName}
      </p>
      <p className="text-muted-foreground text-[11px]">{row.purpose}</p>

      <code className="text-muted-foreground bg-muted/50 mt-2 block overflow-x-auto rounded px-2 py-1 text-[11px]">
        {row.equation}
      </code>

      {row.weights.length > 0 && (
        <ul className="mt-2 space-y-1">
          {row.weights.map((w) => (
            <li key={w.symbol} className="text-[11px]">
              <span className="text-foreground font-semibold">
                weight {w.symbol}
              </span>
              <span className="text-muted-foreground"> = </span>
              <span className="text-foreground tabular-nums">{w.value}</span>
              <span className="text-muted-foreground"> — {w.role}</span>
            </li>
          ))}
        </ul>
      )}

      <ul className="mt-2 space-y-1">
        {row.inputs.map((input) => (
          <li key={input.name} className="text-[11px]">
            <span className="text-foreground font-medium">{input.name}</span>
            <span className="text-muted-foreground"> = </span>
            <span className="text-foreground tabular-nums">{input.value}</span>
            <span className="text-muted-foreground"> — {input.note}</span>
          </li>
        ))}
      </ul>

      <p className="text-muted-foreground mt-2 text-[11px]">
        Output:{' '}
        <span className="text-foreground font-medium">{row.output}</span>
      </p>
    </div>
  )
}

function LogEntry({ entry }: { entry: CalculationLogEntry }) {
  const { deleteCalculation, redoCalculation, statusOf, engineEnabled } =
    useRecommendationEngineMock()
  const [open, setOpen] = useState(false)
  const isReverted = statusOf(entry.id) === 'reverted'
  const isReplay = entry.kind === 'replay'
  const redoLabel = isReverted
    ? 'Re-apply this calculation'
    : 'Redo this calculation'

  const iconBg =
    entry.source === 'attempt'
      ? 'bg-primary/10 text-primary'
      : entry.source === 'coach_adjustment'
        ? 'bg-purple-500/15 text-purple-600 dark:text-purple-400'
        : entry.source === 'seed'
          ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
          : entry.source === 'engine_recompute'
            ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
            : 'bg-sky-500/15 text-sky-600 dark:text-sky-400'

  return (
    <div
      className={cn(
        'border-border overflow-hidden rounded-lg border',
        isReverted && 'opacity-60',
      )}
    >
      <div className="flex items-start">
        <button
          type="button"
          data-testid={`calc-log-entry-${entry.reasonCode}`}
          onClick={() => setOpen(!open)}
          className="hover:bg-muted/50 flex flex-1 items-start gap-3 p-3 text-left transition-colors"
        >
          {open ? (
            <ChevronDown
              size={16}
              className="text-muted-foreground mt-0.5 shrink-0"
            />
          ) : (
            <ChevronRight
              size={16}
              className="text-muted-foreground mt-0.5 shrink-0"
            />
          )}
          <div
            className={cn('mt-0.5 shrink-0 rounded-md p-1.5', iconBg)}
            aria-hidden="true"
          >
            {SOURCE_ICON[entry.source]}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant={
                  entry.source === 'attempt'
                    ? 'topic'
                    : entry.source === 'coach_adjustment'
                      ? 'premium'
                      : 'default'
                }
              >
                {SOURCE_LABEL[entry.source]}
              </Badge>
              <Badge variant="default">{REASON_LABEL[entry.reasonCode]}</Badge>
              {isReplay && <Badge variant="premium">Replay</Badge>}
              {isReverted && <Badge variant="archived">Reverted</Badge>}
              {entry.replacesEntryIds.length > 0 && (
                <span className="text-muted-foreground inline-flex items-center gap-1 text-[11px]">
                  <Link2 size={11} />
                  Replaces {entry.replacesEntryIds.length}
                </span>
              )}
              <span
                className={cn(
                  'text-foreground text-xs font-medium',
                  isReverted && 'line-through',
                )}
              >
                {entry.actorName}
              </span>
              <span className="text-muted-foreground text-[11px]">
                ({ACTOR_LABEL[entry.actorKind]})
              </span>
              {isAttemptEntry(entry) && (
                <Badge variant="default">
                  Grade {entry.grade} · {GRADE_LABEL[entry.grade]}
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground mt-1 text-xs">
              {entry.summary}
            </p>
            <p className="text-muted-foreground/80 mt-0.5 text-[11px] italic">
              Why this triggered: {entry.rationale}
            </p>
            {isAttemptEntry(entry) && (
              <p className="text-muted-foreground/70 mt-0.5 text-[11px]">
                {entry.problemSlug} · {entry.problemSource} ·{' '}
                {entry.durationMinutes}m · {entry.runCount} runs ·{' '}
                {entry.submitCount} submits
              </p>
            )}
            <p className="text-muted-foreground/70 mt-0.5 font-mono text-[10px]">
              Source:{' '}
              {formatCalcLogProvenance(entry.sourceTable, entry.sourceRowId)}
            </p>
          </div>
          <span className="text-muted-foreground shrink-0 text-[11px]">
            {format(new Date(entry.occurredAt), 'MMM d, h:mm a')}
          </span>
        </button>
        <div className="flex shrink-0 items-center gap-1 p-3">
          {!isReverted && (
            <button
              type="button"
              onClick={() => deleteCalculation(entry.id)}
              aria-label="Delete (revert) this calculation"
              title="Delete (revert) this calculation"
              disabled={!engineEnabled}
              className="text-muted-foreground hover:bg-muted rounded-md p-1.5 transition-colors hover:text-rose-500"
            >
              <Trash2 size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={() => redoCalculation(entry.id)}
            aria-label={redoLabel}
            title={redoLabel}
            disabled={!engineEnabled}
            className="text-muted-foreground hover:text-foreground hover:bg-muted rounded-md p-1.5 transition-colors"
          >
            <RotateCcw size={14} />
          </button>
        </div>
      </div>

      {open && (
        <div className="border-border bg-muted/20 space-y-3 border-t p-3">
          {(['subtopic', 'topic', 'overall', 'projection'] as const).map(
            (kind) => {
              const rows = entry.rowsAffected.filter(
                (r) => r.targetKind === kind,
              )
              if (rows.length === 0) return null
              return (
                <div key={kind} className="space-y-2">
                  <p className="text-foreground text-xs font-semibold">
                    {GROUP_LABEL[kind]} ({rows.length})
                  </p>
                  {rows.map((row, i) => (
                    <RowChange
                      key={`${kind}-${row.targetName}-${row.field}-${i}`}
                      row={row}
                      reverted={isReverted}
                    />
                  ))}
                </div>
              )
            },
          )}
          {entry.snapshotDiff && entry.snapshotDiff.length > 0 && (
            <div className="space-y-2">
              <p className="text-foreground text-xs font-semibold">
                Snapshot diff ({entry.snapshotDiff.length})
              </p>
              <div className="border-border overflow-hidden rounded-md border">
                <div className="bg-muted/40 grid grid-cols-[1.5fr_1fr_1fr] gap-2 px-3 py-2 text-[11px] font-semibold">
                  <span>Setting</span>
                  <span>Previous</span>
                  <span>Current</span>
                </div>
                {entry.snapshotDiff.map((row) => (
                  <div
                    key={`${entry.id}-${row.label}`}
                    className="border-border grid grid-cols-[1.5fr_1fr_1fr] gap-2 border-t px-3 py-2 text-[11px]"
                  >
                    <span className="text-foreground">{row.label}</span>
                    <span className="text-muted-foreground tabular-nums">
                      {row.previousValue}
                    </span>
                    <span className="text-foreground tabular-nums">
                      {row.currentValue}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {entry.reasonCode === 'graph_json_saved' &&
            entry.source === 'coach_adjustment' && (
              <div className="space-y-2">
                {entry.graphJsonReasonMetadata?.role === 'propagated' && (
                  <p className="text-muted-foreground rounded-md border border-dashed px-3 py-2 text-xs">
                    Global graph JSON updated by{' '}
                    {entry.graphJsonReasonMetadata.originCoachName ??
                      'Unknown coach'}
                    {entry.graphJsonReasonMetadata.originCoachUserId
                      ? ` (${entry.graphJsonReasonMetadata.originCoachUserId})`
                      : ''}{' '}
                    while viewing client{' '}
                    {entry.graphJsonReasonMetadata.originClientId ??
                      'unknown client'}
                    .
                  </p>
                )}
                {'coachUserId' in entry && entry.coachUserId && (
                  <p className="text-muted-foreground text-[11px]">
                    Coach user id: {entry.coachUserId}
                  </p>
                )}
                {'valueBefore' in entry && entry.valueBefore !== undefined && (
                  <details className="text-xs">
                    <summary className="text-foreground cursor-pointer font-medium">
                      Previous graph JSON snapshot
                    </summary>
                    <pre className="bg-muted/40 mt-2 max-h-48 overflow-auto rounded-md p-2 font-mono text-[10px]">
                      {JSON.stringify(entry.valueBefore, null, 2)}
                    </pre>
                  </details>
                )}
                {'valueAfter' in entry && entry.valueAfter !== undefined && (
                  <details className="text-xs">
                    <summary className="text-foreground cursor-pointer font-medium">
                      Updated graph JSON snapshot
                    </summary>
                    <pre className="bg-muted/40 mt-2 max-h-48 overflow-auto rounded-md p-2 font-mono text-[10px]">
                      {JSON.stringify(entry.valueAfter, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            )}
        </div>
      )}
    </div>
  )
}

/**
 * Calculation Log view — the human-readable audit trail.
 *
 * Every entry is append-only: revert and re-apply emit NEW entries rather
 * than mutating existing ones. The "Reverted" badge is derived (via
 * `statusOf`) from the existence of a later `coach_revert` event pointing
 * at the entry. This is what lets us promise a coach never sees a regression
 * or hallucinated data: because nothing is mutated or removed, every state the
 * UI has ever shown stays reconstructible from the log itself.
 */
export function CalculationLogView() {
  const { calculationLog } = useRecommendationEngineMock()
  const [sourceFilter, setSourceFilter] = useState<string>('')
  const [kindFilter, setKindFilter] = useState<'' | 'original' | 'replay'>('')
  const [reasonFilters, setReasonFilters] = useState<ReasonCode[]>([])
  const [entityFilter, setEntityFilter] = useState<'' | CalculationTargetKind>(
    '',
  )
  const [actorQuery, setActorQuery] = useState('')
  const [snapshotOpen, setSnapshotOpen] = useState(false)

  const entries = useMemo(() => {
    const sorted = [...calculationLog].sort(
      (a, b) =>
        new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
    )
    const q = actorQuery.trim().toLowerCase()
    return sorted.filter((e) => {
      if (sourceFilter && e.source !== sourceFilter) return false
      if (kindFilter && e.kind !== kindFilter) return false
      if (reasonFilters.length > 0 && !reasonFilters.includes(e.reasonCode)) {
        return false
      }
      if (q && !e.actorName.toLowerCase().includes(q)) return false
      if (entityFilter) {
        const has = e.rowsAffected.some((r) => r.targetKind === entityFilter)
        if (!has) return false
      }
      return true
    })
  }, [
    calculationLog,
    sourceFilter,
    kindFilter,
    reasonFilters,
    entityFilter,
    actorQuery,
  ])

  return (
    <div className="space-y-4">
      <RecalculatingBanner />

      {/* Current-state snapshot pane: per-subtopic FSRS internals with live
          R(t,S). Collapsed by default — the event stream is the primary
          audit surface; the snapshot is reference state. */}
      <div className="border-border overflow-hidden rounded-lg border">
        <button
          type="button"
          onClick={() => setSnapshotOpen((v) => !v)}
          aria-label={
            snapshotOpen
              ? 'Hide knowledge map snapshot'
              : 'Show knowledge map snapshot'
          }
          aria-expanded={snapshotOpen}
          className="hover:bg-muted/50 flex w-full items-center gap-2 p-3 text-left transition-colors"
        >
          {snapshotOpen ? (
            <ChevronDown size={16} className="text-muted-foreground" />
          ) : (
            <ChevronRight size={16} className="text-muted-foreground" />
          )}
          <div>
            <p className="text-foreground text-sm font-semibold">
              Knowledge map snapshot
            </p>
            <p className="text-muted-foreground text-xs">
              Per-subtopic mastery, live R(t,S), effective mastery, stability,
              difficulty, and last-attempt time. Read-only — set values via the
              Setup tab&apos;s seed tool.
            </p>
          </div>
        </button>
        {snapshotOpen && (
          <div className="border-border border-t p-3">
            <KnowledgeMapSnapshot scope="all" />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <p className="text-muted-foreground text-xs">
          Append-only audit trail of every change to this client&apos;s
          knowledge map. Every entry is backed by a row in either
          AttemptEventLog or CoachAdjustmentLog and is never mutated — revert
          and re-apply append new entries.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex min-w-[140px] flex-col gap-1">
            <label
              htmlFor="calc-filter-source"
              className="text-muted-foreground text-[11px] font-medium"
            >
              Source
            </label>
            <select
              id="calc-filter-source"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="bg-muted border-border text-foreground rounded-lg border px-2.5 py-1.5 text-sm"
              aria-label="Filter by source"
            >
              <option value="">All sources</option>
              <option value="attempt">Student attempts</option>
              <option value="coach_adjustment">Coach adjustments</option>
              <option value="seed">Knowledge map seed</option>
              <option value="engine_recompute">Engine recomputes</option>
              <option value="recommendation_lifecycle">
                Recommendation lifecycle
              </option>
            </select>
          </div>
          <div className="flex min-w-[120px] flex-col gap-1">
            <label
              htmlFor="calc-filter-kind"
              className="text-muted-foreground text-[11px] font-medium"
            >
              Kind
            </label>
            <select
              id="calc-filter-kind"
              value={kindFilter}
              onChange={(e) =>
                setKindFilter(e.target.value as '' | 'original' | 'replay')
              }
              className="bg-muted border-border text-foreground rounded-lg border px-2.5 py-1.5 text-sm"
              aria-label="Filter by entry kind"
            >
              <option value="">Original + replay</option>
              <option value="original">Original only</option>
              <option value="replay">Replay only</option>
            </select>
          </div>
          <div className="flex min-w-[220px] max-w-[min(100%,280px)] flex-col gap-1">
            <label
              htmlFor="calc-filter-reason"
              className="text-muted-foreground text-[11px] font-medium"
            >
              Reason codes
            </label>
            <select
              id="calc-filter-reason"
              multiple
              size={6}
              value={reasonFilters}
              onChange={(e) => {
                const selected = Array.from(
                  e.target.selectedOptions,
                  (o) => o.value as ReasonCode,
                )
                setReasonFilters(selected)
              }}
              className="bg-muted border-border text-foreground rounded-lg border px-2 py-1.5 text-sm"
              aria-label="Filter by one or more reason codes"
            >
              {REASON_CODES.map((code) => (
                <option key={code} value={code}>
                  {REASON_LABEL[code]}
                </option>
              ))}
            </select>
            <p className="text-muted-foreground text-[10px] leading-snug">
              Cmd/Ctrl-click for multiple. None selected = all reasons.
            </p>
          </div>
          <div className="flex min-w-[130px] flex-col gap-1">
            <label
              htmlFor="calc-filter-entity"
              className="text-muted-foreground text-[11px] font-medium"
            >
              Row target
            </label>
            <select
              id="calc-filter-entity"
              value={entityFilter}
              onChange={(e) =>
                setEntityFilter(
                  (e.target.value || '') as '' | CalculationTargetKind,
                )
              }
              className="bg-muted border-border text-foreground rounded-lg border px-2.5 py-1.5 text-sm"
              aria-label="Filter by affected row kind"
            >
              <option value="">Any target</option>
              <option value="subtopic">Subtopic rows</option>
              <option value="topic">Topic rows</option>
              <option value="overall">Overall</option>
              <option value="projection">Projection</option>
            </select>
          </div>
          <div className="flex min-w-[160px] flex-1 flex-col gap-1">
            <label
              htmlFor="calc-filter-actor"
              className="text-muted-foreground text-[11px] font-medium"
            >
              Actor search
            </label>
            <input
              id="calc-filter-actor"
              type="search"
              value={actorQuery}
              onChange={(e) => setActorQuery(e.target.value)}
              placeholder="Name contains…"
              className="bg-muted border-border text-foreground rounded-lg border px-2.5 py-1.5 text-sm"
              aria-label="Search by actor name"
            />
          </div>
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="text-muted-foreground border-border rounded-lg border border-dashed py-10 text-center text-sm">
          No log entries match the current filter.
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => (
            <LogEntry key={entry.id} entry={entry} />
          ))}
        </div>
      )}
    </div>
  )
}
